/**
 * SYM-6273. The one length the wizard shows for a video, everywhere it shows one.
 *
 * Two numbers existed. The timer under the preview showed the length of the
 * plan the video is made from. The summary rows showed the slides added up,
 * which ignores the overlap every changeover takes out of the slide before it
 * (three slides of 3.2 s are an 8.4 s video, not 9.6), and printed that sum
 * the way the computer holds it: "9.600000000000001s".
 *
 * So: when we have the plan of the video as it stands now, its length is the
 * answer. Adding the slides up is only the fallback for when there is no such
 * plan -- the Creatomate player, or a preview that an edit has made out of
 * date -- and it is rounded the same way.
 */
export interface ShownDurationInput {
  /** The slides as the wizard holds them now. */
  sceneDurations: number[];
  /** Length of our own preview plan, when there is one. */
  planDuration?: number | null;
  /** False when an edit was made after that plan was built. */
  planIsCurrent: boolean;
}

/** Seconds, to one decimal, without a unit: "8.4". */
export function shownDuration({
  sceneDurations,
  planDuration,
  planIsCurrent,
}: ShownDurationInput): string {
  const hasPlan =
    typeof planDuration === 'number' &&
    Number.isFinite(planDuration) &&
    planDuration > 0;

  if (hasPlan && planIsCurrent) {
    return (planDuration as number).toFixed(1);
  }

  const sum = sceneDurations.reduce(
    (total, seconds) => total + (Number.isFinite(seconds) ? seconds : 0),
    0
  );
  return sum.toFixed(1);
}
